import { useTranslation } from 'react-i18next';
import { UserSearch } from 'lucide-react';
import type { DuplicateCheckResult } from '@optiflow/types';
import { Button } from '@/components/ui/Button';
import { CustomerStatusBadge } from './CustomerStatusBadge';

interface DuplicateCustomerWarningProps {
  result: DuplicateCheckResult;
  onUseExisting: (customerId: string) => void;
  onCreateAnyway: () => void;
  isCreating?: boolean;
}

/**
 * Shown before a customer is created when something similar already exists.
 *
 * Advisory, never blocking: a parent and child sharing one mobile is a legitimate case, and
 * refusing it would push staff into inventing fake numbers. The job here is to make sure nobody
 * creates a duplicate *without having seen* the existing record.
 */
export function DuplicateCustomerWarning({
  result,
  onUseExisting,
  onCreateAnyway,
  isCreating = false,
}: DuplicateCustomerWarningProps) {
  const { t } = useTranslation();

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
        <UserSearch className="mt-0.5 size-4 shrink-0 text-amber-700" aria-hidden />
        <div>
          <p className="text-sm font-medium text-amber-900">{t('customers.duplicates.title')}</p>
          <p className="mt-0.5 text-sm text-amber-800">{t('customers.duplicates.description')}</p>
        </div>
      </div>

      <ul className="divide-line border-line divide-y rounded-lg border">
        {result.matches.map((match) => (
          <li key={match.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-ink truncate text-sm font-medium">{match.displayName}</span>
                <CustomerStatusBadge status={match.status} />
              </div>
              <p className="text-muted mt-0.5 text-xs">
                <span dir="ltr">{match.mobile}</span>
                <span className="mx-1.5">·</span>
                <span className="font-mono">{match.customerNumber}</span>
              </p>
              <p className="text-muted mt-0.5 text-xs">
                {t(`customers.duplicates.reason.${match.reason.toLowerCase()}`)}
              </p>
            </div>

            <Button variant="secondary" size="sm" onClick={() => onUseExisting(match.id)}>
              {t('customers.duplicates.useExisting')}
            </Button>
          </li>
        ))}
      </ul>

      <Button variant="secondary" fullWidth onClick={onCreateAnyway} isLoading={isCreating}>
        {t('customers.duplicates.createAnyway')}
      </Button>
    </div>
  );
}
